import { useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { Sparkles, Leaf, Coins, ScrollText, Play, X } from "lucide-react";

export default function Landing() {
  const { user, login } = useAuth();
  const [showDemo, setShowDemo] = useState(false);
  return (
    <main className="pt-24">
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10 opacity-30" style={{
          background: "radial-gradient(800px 500px at 70% 20%, rgba(212,175,55,0.25), transparent 60%), radial-gradient(700px 600px at 10% 80%, rgba(46,82,58,0.4), transparent 60%)"
        }} />
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-24 grid md:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-gold mb-6 flex items-center gap-2"><Leaf size={14}/> Ritual sagrado da Terra</p>
            <h1 className="font-display text-5xl md:text-7xl leading-[1.05] mb-6">
              Compra a tua carta. <br />
              <span className="shimmer-text">Revela o teu destino.</span>
            </h1>
            <p className="text-[color:var(--text-2)] text-lg leading-relaxed max-w-xl mb-10">
              33 cartas sagradas. Comuns, raras, épicas e míticas. Adquire as cartas que quiseres, escolhe do baralho virado e descobre se a Gaia te recompensa com um voucher para trocar por prémios.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/sorteio" className="btn-primary" data-testid="hero-cta-sorteio">Entrar no Ritual</Link>
              <button onClick={() => setShowDemo(true)} className="btn-secondary flex items-center gap-2" data-testid="hero-watch-demo">
                <Play size={16}/> Ver Demonstração
              </button>
              {!user && <button onClick={login} className="btn-secondary" data-testid="hero-login-button">Entrar</button>}
            </div>
          </div>
          <div className="relative">
            <div className="grid grid-cols-3 gap-3 max-w-md mx-auto">
              {[31, 16, 1, 26, 32, 6, 13, 22, 33].map((id, i) => (
                <img key={id} src={`/cards/card-${String(id).padStart(2,'0')}.jpg`} alt=""
                  className="rounded-lg border border-gold/30 shadow-xl"
                  style={{transform: `rotate(${(i%3 - 1) * 3}deg)`}} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Inline demo preview */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 -mt-4 mb-8">
        <div className="glass overflow-hidden rounded-2xl border border-gold/30 relative cursor-pointer group" onClick={() => setShowDemo(true)} data-testid="demo-preview-card">
          <img src="/demo.gif" alt="Demonstração do ritual" className="w-full max-h-[520px] object-cover" />
          <div className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/40 transition-colors">
            <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-3 text-gold font-display text-2xl bg-[color:var(--bg)]/80 px-6 py-3 rounded-full border border-gold/40">
              <Play size={22}/> Ver em ecrã cheio
            </div>
          </div>
          <div className="absolute top-4 left-4 text-xs uppercase tracking-[0.3em] text-gold bg-black/60 px-3 py-1 rounded-full border border-gold/30">Demonstração</div>
        </div>
      </section>

      {/* Demo Modal */}
      {showDemo && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={() => setShowDemo(false)} data-testid="demo-modal">
          <button onClick={() => setShowDemo(false)} className="absolute top-6 right-6 text-gold hover:text-white" data-testid="demo-close"><X size={28}/></button>
          <div className="w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <video src="/demo.mp4" autoPlay loop controls playsInline className="w-full rounded-2xl border border-gold/30 shadow-[0_0_60px_rgba(212,175,55,0.2)]" />
            <p className="text-center text-[color:var(--text-2)] mt-4 text-sm">Compra cartas → revela do baralho → gira a roleta → troca por prémios</p>
          </div>
        </div>
      )}

      {/* Steps */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid md:grid-cols-4 gap-6">
        {[
          {icon: Coins, t: "1. Compra", d: "Escolhe quantas cartas queres adquirir. 10€ cada carta."},
          {icon: ScrollText, t: "2. Pagamento", d: "MBWay, PayPal, transferência ou crypto. Confirmação manual."},
          {icon: Sparkles, t: "3. Revela", d: "Escolhe cartas do baralho virado para baixo. Vira-as e descobre."},
          {icon: Leaf, t: "4. Voucher", d: "Roleta decide o valor. Troca por prémios na loja."},
        ].map((s, i) => (
          <div key={i} className="glass p-6">
            <s.icon className="text-gold mb-4" size={28} />
            <h3 className="font-display text-2xl mb-2">{s.t}</h3>
            <p className="text-[color:var(--text-2)] text-sm leading-relaxed">{s.d}</p>
          </div>
        ))}
      </section>

      {/* Rarity */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <h2 className="font-display text-4xl md:text-5xl mb-10 text-center">As raridades de Gaia</h2>
        <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-6">
          {[
            {n: "Comuns", c: 15, p: 25, color: "rarity-common"},
            {n: "Raras", c: 10, p: 50, color: "rarity-rare"},
            {n: "Épicas", c: 5, p: 75, color: "rarity-epic"},
            {n: "Míticas", c: 3, p: 100, color: "rarity-mythic"},
          ].map(r => (
            <div key={r.n} className={`glass p-6 border-2 ${r.color}`}>
              <div className="font-display text-3xl">{r.n}</div>
              <div className="text-[color:var(--text-2)] text-sm mt-1">{r.c} cartas no baralho</div>
              <div className="text-gold text-4xl font-display mt-4">{r.p}%</div>
              <div className="text-[color:var(--text-3)] text-xs uppercase tracking-widest">prob. de prémio</div>
            </div>
          ))}
        </div>
      </section>
      <footer className="border-t border-gold/10 py-8 mt-12 text-center text-[color:var(--text-3)] text-sm">
        © {new Date().getFullYear()} Triunfo de Gaia — Ritual digital da Terra
      </footer>
    </main>
  );
}
